// LEXICAL search over items and locations: the half of search that deals in the words that
// are literally there. The other half — "utensils" finding the thing labelled "silverware" —
// is semantic and cannot be done here at any quality; see semantic.js, which fuses its
// results with these. This tier is not a fallback. It is the one that answers instantly with
// no network, it is the only one that can be sure "hammer" means the item called hammer, and
// it is what the search box shows while the semantic results are still in flight.
//
// What this replaced: Fuse.js over two fields — an item's label and the name of its
// IMMEDIATE location. That failed in four ways people hit constantly:
//
//   • "basement screwdriver" found nothing. Only the immediate parent's name was indexed,
//     so anything about where a thing actually lives ("garage", "kitchen", the shelf two
//     levels up) was invisible to search.
//   • A multi-word query was scored as one fuzzy blob against each field independently, so
//     "red pencil" could rank "red pen" above "pencil (red)". Terms are now scored one at a
//     time and every term has to match something.
//   • An exact match could lose to a fuzzy one, because a weighted average of per-field
//     scores has no notion of "this is literally the word you typed".
//   • "batteries" did not find "battery".
//
// So: a small deterministic scorer instead of a fuzzy-matching library. Tiers (exact word >
// prefix > one typo > substring), fields weighted so the label dominates the immediate
// location and that dominates the rest of the path, AND semantics across terms, and English
// plural folding. Being deterministic is the point — it is why the behaviour above can be
// asserted in tests at all, which Fuse's opaque relevance scores could not be.

import { normalizeText, tokenize, foldPlural, withinEditDistance } from "./text.js";

// Field weights. A term matching the label is worth ~3x the same term matching the location
// name, which is worth ~1.7x matching further up the path — so "hammer" in the label always
// beats a location called "Hammer shelf", but the location still counts for something.
//
// A tag sits between the two: it is a deliberate statement about the thing itself, so it counts for
// more than the room the thing is in, but the name is still what the thing IS. This is the only place
// a tag someone invented can be found at all — curated tags reach a query through their embedded
// exemplars in match_tags, and a user's own tag has no embedding, so its words are all it has. That
// makes "wrapping paper" find the box tagged `wrapping-paper` even though nothing is called that.
const W_LABEL = 1;
const W_TAG = 0.5;
const W_LOCATION = 0.34;
const W_PATH = 0.2;

// Match tiers, best first. A single typo scores above a coincidental substring on purpose:
// "pencl" against "pencil" is a strong signal, "en" inside "kitchen" is not.
const T_EXACT = 100;
const T_PREFIX = 55;
const T_TYPO = 34;
const T_SUBSTRING = 22;

// Only long enough words get typo tolerance; at three characters an edit is a different word.
const TYPO_MIN_LENGTH = 4;
const TYPO_TWO_EDITS_MIN_LENGTH = 8;

export { normalizeText, tokenize };

function typoBudget(term) {
  if (term.length >= TYPO_TWO_EDITS_MIN_LENGTH) return 2;
  if (term.length >= TYPO_MIN_LENGTH) return 1;
  return 0;
}

// Best tier at which `term` matches one field of normalised text. Returns 0 for no match.
// Checked in descending tier order, so the first hit is the best one.
function tierForField(term, text, words) {
  if (!text) return 0;
  const folded = foldPlural(term);
  for (const word of words) {
    if (word === term || foldPlural(word) === folded) return T_EXACT;
  }
  for (const word of words) {
    if (word.startsWith(term)) return T_PREFIX;
  }
  const budget = typoBudget(term);
  if (budget > 0) {
    for (const word of words) {
      if (withinEditDistance(term, word, budget)) return T_TYPO;
    }
  }
  if (term.length >= 2 && text.includes(term)) return T_SUBSTRING;
  return 0;
}

// Pre-split the searchable text once per record rather than once per term.
function prepareFields(fields) {
  return fields.map(({ text, weight }) => {
    const norm = normalizeText(text);
    return { text: norm, words: norm ? norm.split(" ") : [], weight };
  });
}

// Score one record against every term. Returns 0 unless EVERY term matches somewhere: an
// inventory search is a filter, and a result that ignores half of what you typed is noise.
// A query whose words are not all present is exactly the case semantic.js exists to answer.
function scorePrepared(prepared, terms, wholeQuery, primary) {
  let total = 0;
  for (const term of terms) {
    let best = 0;
    for (const field of prepared) {
      const tier = tierForField(term, field.text, field.words);
      if (tier) best = Math.max(best, tier * field.weight);
    }
    if (!best) return 0;
    total += best;
  }
  // Whole-query bonuses on the primary field, so the thing you literally typed wins even
  // when a longer record accumulates more per-term points.
  if (primary && wholeQuery) {
    if (primary === wholeQuery) total += 600;
    else if (primary.startsWith(wholeQuery + " ")) total += 150;
    else if (primary.includes(wholeQuery)) total += 50;
  }
  return total;
}

// Slugs as the words they were before they were slugs, so `wrapping-paper` matches "paper".
function tagText(tags) {
  if (!Array.isArray(tags) || !tags.length) return "";
  return tags.filter((tag) => typeof tag === "string").join(" ").replace(/-/g, " ");
}

function rank(scored, extraBoost) {
  return scored
    .map((entry) => ({ ...entry, score: entry.score + (extraBoost ? extraBoost(entry.record) || 0 : 0) }))
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score || a.tieLength - b.tieLength || a.index - b.index)
    .map((entry) => entry.record);
}

// Filter + rank items by a query. Empty query returns the list untouched, which is what
// every call site relies on to render its normal browse view.
//
// Items are expected to carry `label`, `location` (immediate location name), `location_path` (the
// full "Garage > Workbench > Top shelf" string) and optionally `tags` (slugs). A missing
// location_path just means the wider path can't be matched, not that search breaks.
//
// extraBoost(item) adds to the score — used to float items in the space you're already in.
export function searchItems(list, query, extraBoost) {
  const terms = tokenize(query);
  if (!terms.length) return list;
  const wholeQuery = normalizeText(query);
  const scored = (list || []).map((record, index) => {
    const label = normalizeText(record.label);
    const prepared = prepareFields([
      { text: record.label, weight: W_LABEL },
      { text: tagText(record.tags), weight: W_TAG },
      { text: record.location, weight: W_LOCATION },
      { text: record.location_path, weight: W_PATH },
    ]);
    return {
      record,
      index,
      tieLength: label.length,
      score: scorePrepared(prepared, terms, wholeQuery, label),
    };
  });
  return rank(scored, extraBoost);
}

// A location search lived here too, so that "workbench" surfaced the place and not only the things on
// it, and every screen listed matching locations above the items. It was removed: from any screen, a
// search is a question about a thing you own, and places in front of the answer are the list the screen
// already is when nothing is typed. The query still reaches location *names* through the vector side —
// that is how "camping utensils" finds the spork in Camping gear — but as a ranking signal on items,
// never as rows of its own. `git log` has the function if a place-finder is ever wanted again.
